export interface MergeVariable {
  key: string;
  placeholder: string;
  label: string;
  description?: string;
}

export interface MergeVariableCategory {
  category: string;
  color: string;
  variables: MergeVariable[];
}

export const MERGE_VARIABLES: MergeVariableCategory[] = [
  {
    category: "Recipient",
    color: "#3b82f6",
    variables: [
      { key: "userName", placeholder: "{{userName}}", label: "Recipient name" },
      {
        key: "userEmail",
        placeholder: "{{userEmail}}",
        label: "Recipient email",
      },
    ],
  },
  {
    category: "Company",
    color: "#8b5cf6",
    variables: [
      {
        key: "companyName",
        placeholder: "{{companyName}}",
        label: "Company name",
      },
      {
        key: "productName",
        placeholder: "{{productName}}",
        label: "Product brand",
      },
      {
        key: "supportEmail",
        placeholder: "{{supportEmail}}",
        label: "Support email",
      },
    ],
  },
];

export interface TemplateZones {
  greeting: string;
  body: string;
  closing: string;
}

export interface SampleTemplate {
  id: string;
  name: string;
  template_code: string;
  subject: string;
  accent: string;
  description: string;
  headerTitle: string;
  headerSubtitle: string;
  headerEyebrow?: string;
  zones: TemplateZones;
  category: string;
  icon?: string;
}

export interface SampleCategory {
  value: string;
  label: string;
  icon: string;
  color: string;
}

export const SAMPLE_CATEGORIES: SampleCategory[] = [
  { value: "all", label: "All", icon: "📚", color: "#1677ff" },
  { value: "general", label: "General", icon: "✉️", color: "#3730a3" },
  { value: "tasks", label: "Tasks", icon: "✅", color: "#3730a3" },
  { value: "leaves", label: "Leaves", icon: "🌴", color: "#16a34a" },
  { value: "attendance", label: "Attendance", icon: "🕒", color: "#0ea5e9" },
  { value: "leads", label: "Lead Activity", icon: "🎯", color: "#dc2626" },
  { value: "reports", label: "Reports", icon: "📊", color: "#8b5cf6" },
  { value: "assets", label: "Assets", icon: "💼", color: "#0f766e" },
  { value: "tickets", label: "Tickets", icon: "🎫", color: "#b45309" },
  { value: "projects", label: "Projects", icon: "📁", color: "#3730a3" },
  {
    value: "calendar",
    label: "Activity Calendar",
    icon: "📅",
    color: "#0ea5e9",
  },
  { value: "drive", label: "File Manager", icon: "📂", color: "#475569" },
  { value: "chat", label: "Chat", icon: "💬", color: "#3b82f6" },
];

const cta = (label: string, color = "#3730a3") =>
  `
<p style="text-align:center;margin:24px 0;">
  <a href="#" style="display:inline-block;padding:13px 30px;background:${color};color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;font-size:14px;letter-spacing:0.02em;">${label}</a>
</p>`.trim();

const dt = (rows: { label: string; value: string }[]) => {
  const cellsHtml = rows
    .map((r, i) => {
      const isLast = i === rows.length - 1;
      const bb = isLast ? "" : "border-bottom:1px solid #e2e8f0;";
      const labelCell = `<td bgcolor="#f8fafc" valign="middle" align="center" width="42%" style="width:42%;padding:14px 16px;font-family:'Inter',Arial,sans-serif;font-size:12px;line-height:18px;font-weight:600;color:#475569;text-transform:uppercase;letter-spacing:0.06em;background-color:#f8fafc;border-right:1px solid #e2e8f0;text-align:center;vertical-align:middle;${bb}">${r.label}</td>`;
      const valueCell = `<td bgcolor="#ffffff" valign="middle" align="left" style="padding:14px 16px;font-family:'Inter',Arial,sans-serif;font-size:13px;line-height:20px;font-weight:500;color:#0f172a;background-color:#ffffff;text-align:left;vertical-align:middle;${bb}">${r.value}</td>`;
      return `<tr>${labelCell}${valueCell}</tr>`;
    })
    .join("\n");
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="width:100%;margin:16px 0;border:1px solid #e2e8f0;border-radius:8px;border-collapse:separate;border-spacing:0;overflow:hidden;background-color:#ffffff;">
<tbody>
${cellsHtml}
</tbody>
</table>`.trim();
};

const callout = (text: string, accent: string) =>
  `
<div style="background-color:${accent}1a;border-left:3px solid ${accent};border-radius:8px;padding:14px 16px;margin:12px 0;font-family:'Inter',Arial,sans-serif;font-size:14px;line-height:22px;font-weight:400;color:#0f172a;">
  ${text}
</div>`.trim();

const otpBlock = (varName: string, expiryVar: string, accent: string) =>
  `
<div style="border:1px dashed #cbd5e1;border-radius:8px;padding:24px;text-align:center;background-color:#fbfcfe;margin:12px 0;">
  <div style="font-family:'Inter',Arial,sans-serif;font-size:11px;line-height:16px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.16em;margin-bottom:14px;">Verification Code</div>
  <div style="font-family:'JetBrains Mono','SF Mono','Menlo',monospace;font-size:38px;line-height:44px;font-weight:700;color:${accent};letter-spacing:0.32em;">{{${varName}}}</div>
  <div style="font-family:'Inter',Arial,sans-serif;font-size:12px;line-height:18px;font-weight:400;color:#94a3b8;margin-top:14px;">Expires in {{${expiryVar}}} minutes &middot; Single use</div>
</div>`.trim();

const THEMES = {
  indigo: "#3730a3",
  amber: "#b45309",
  red: "#dc2626",
  green: "#16a34a",
  teal: "#0f766e",
  slate: "#475569",
  blue: "#3b82f6",
  rose: "#e11d48",
  violet: "#8b5cf6",
};

interface Spec {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  subject: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  greeting?: string;
  bodyHtml: string;
  closing?: string;
  accent?: string;
}

const make = (s: Spec): SampleTemplate => ({
  id: s.id,
  name: s.name,
  template_code: s.id,
  subject: s.subject,
  accent: s.accent || THEMES.indigo,
  category: s.category,
  icon: s.icon,
  description: s.description,
  headerTitle: s.title,
  headerSubtitle: s.subtitle,
  headerEyebrow: s.eyebrow,
  zones: {
    greeting: s.greeting || "<p>Hi <strong>{{userName}}</strong>,</p>",
    body: s.bodyHtml,
    closing: s.closing || "<p>Best regards,<br/>The {{companyName}} Team</p>",
  },
});

export const SAMPLE_TEMPLATES: SampleTemplate[] = [
  // ----- GENERAL -----
  make({
    id: "blank_page",
    name: "Blank",
    category: "general",
    icon: "✏️",
    description: "Empty starting point — three zones ready to fill.",
    subject: "Subject line goes here",
    eyebrow: "",
    title: "Your email title",
    subtitle: "",
    bodyHtml: "<p>Write your message here…</p>",
  }),

  // ----- TASKS -----
  make({
    id: "task_assigned_sample",
    name: "Task Assigned",
    category: "tasks",
    icon: "📋",
    description: "New task notification with details and CTA.",
    subject: "New Task Assigned: {{taskTitle}}",
    eyebrow: "Assigned by {{assignedByName}}",
    title: "{{taskTitle}}",
    subtitle: "A new task has been assigned to you.",
    bodyHtml: `${callout("📋 <strong>Description:</strong> {{taskDescription}}", THEMES.indigo)}${dt(
      [
        { label: "👤 Assigned by", value: "{{assignedByName}}" },
        { label: "📅 Deadline", value: "{{dueDate}}" },
      ],
    )}${cta("View task on dashboard")}<p>Every great achievement starts with a single task — this is yours! 🌟</p>`,
  }),
  make({
    id: "task_follower_added",
    name: "Added as Follower",
    category: "tasks",
    icon: "👀",
    description: "Notify a follower added to a task.",
    subject: "You've been added as a task follower",
    eyebrow: "Stay in the loop",
    title: "You're following {{taskTitle}}",
    subtitle: "Keep an eye on the progress and jump in when needed.",
    bodyHtml: `${dt([
      { label: "📋 Task", value: "{{taskTitle}}" },
      { label: "👤 Assigned to", value: "{{assigneeName}}" },
      { label: "👤 Assigned by", value: "{{assignedByName}}" },
      { label: "📅 Due", value: "{{dueDate}}" },
    ])}${cta("View task on dashboard")}`,
  }),
  make({
    id: "task_in_progress",
    name: "Task → In Progress",
    category: "tasks",
    icon: "⚡",
    description: "Status change to In Progress.",
    subject: "Task Status Updated: {{taskTitle}}",
    eyebrow: "Status updated",
    title: "{{taskTitle}}",
    subtitle: "Pending → In Progress",
    bodyHtml: `<p>You're making progress — keep that momentum going! 🚀</p>${dt(
      [
        {
          label: "🔄 Status",
          value:
            "<span style='color:#3730a3;font-weight:600;'>In Progress</span>",
        },
        { label: "📅 Deadline", value: "{{dueDate}}" },
        { label: "👤 Assigned by", value: "{{assignedByName}}" },
      ],
    )}${cta("Continue on dashboard")}`,
  }),
  make({
    id: "task_on_hold",
    name: "Task → On Hold",
    category: "tasks",
    icon: "⏸️",
    description: "Status change to On Hold with reason.",
    subject: "Task On Hold: {{taskTitle}}",
    eyebrow: "Pause",
    title: "{{taskTitle}}",
    subtitle: "This task has been put on hold.",
    accent: THEMES.amber,
    bodyHtml: `${callout("📌 <strong>Reason:</strong> {{holdReason}}", THEMES.amber)}${dt(
      [
        { label: "📋 Task", value: "{{taskTitle}}" },
        { label: "👤 Assigned by", value: "{{assignedByName}}" },
        { label: "📅 Deadline", value: "{{dueDate}}" },
      ],
    )}<p>Please stay available and keep an eye out for updates.</p>${cta("View on dashboard", THEMES.amber)}`,
  }),
  make({
    id: "task_completed",
    name: "Task → Completed",
    category: "tasks",
    icon: "✅",
    description: "Celebrate task completion.",
    subject: "Task Completed — Well done! 🎉",
    eyebrow: "Completed",
    title: "{{taskTitle}}",
    subtitle: "In Progress → Completed",
    accent: THEMES.green,
    bodyHtml: `<p>You crushed it! 💪 Your hard work and dedication truly make a difference — this is what excellence looks like! 🌟</p>${dt(
      [
        { label: "👤 Assigned by", value: "{{assignedByName}}" },
        {
          label: "🏆 Status",
          value:
            "<span style='color:#16a34a;font-weight:600;'>Completed</span>",
        },
      ],
    )}${cta("View on dashboard", THEMES.green)}`,
  }),
  make({
    id: "task_overdue_sample",
    name: "Task Overdue",
    category: "tasks",
    icon: "⚠️",
    description: "Urgent reminder for overdue tasks.",
    subject: "Action Required: Overdue Task",
    eyebrow: "Action needed",
    title: "You have an overdue task",
    subtitle: "This task has passed its due date.",
    accent: THEMES.red,
    bodyHtml: `${dt([
      { label: "📋 Task", value: "{{taskTitle}}" },
      { label: "📋 Description", value: "{{taskDescription}}" },
      { label: "📅 Due Date", value: "{{dueDate}}" },
      { label: "👤 Assigned by", value: "{{assignedByName}}" },
    ])}<p>If you're unable to complete this today or need a timeline change, please report to your manager to avoid further delays.</p>${cta("View & update task", THEMES.red)}`,
  }),
  make({
    id: "task_comment",
    name: "Task Comment",
    category: "tasks",
    icon: "💬",
    description: "New comment on a task.",
    subject: "New Comment on Task: {{taskTitle}}",
    eyebrow: "New activity",
    title: "{{commenterName}} left a comment",
    subtitle: "Head over and keep the conversation going.",
    bodyHtml: `<blockquote style="border-left:3px solid #3730a3;padding:10px 14px;color:#475569;background:#f8fafc;border-radius:0 6px 6px 0;margin:12px 0;">{{commentText}}</blockquote>${cta("Open task")}`,
  }),
  make({
    id: "task_due_tomorrow_sample",
    name: "Tasks Due Tomorrow",
    category: "tasks",
    icon: "⏰",
    description: "Daily reminder of tomorrow's tasks.",
    subject: "⏰ Reminder: tasks due tomorrow",
    eyebrow: "Heads up",
    title: "You have tasks due tomorrow",
    subtitle: "Please complete or update before the deadline.",
    accent: THEMES.amber,
    bodyHtml: `<div style="font:600 13px/20px 'Inter',Arial,sans-serif;color:#0f172a;margin:8px 0 12px;">📋 Coming up</div><div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#ffffff;">{{taskListHtml}}</div>${cta("Open my tasks", THEMES.amber)}`,
  }),
  make({
    id: "task_due_date_changed",
    name: "Task Due Date Changed",
    category: "tasks",
    icon: "📅",
    description: "Notify of revised task deadline.",
    subject: "Task Due Date Updated",
    eyebrow: "Heads up",
    title: "Deadline changed for {{taskTitle}}",
    subtitle: "Adjust your schedule and stay on track.",
    bodyHtml: `${dt([
      { label: "📋 Task", value: "{{taskTitle}}" },
      { label: "📅 New Deadline", value: "{{newDueDate}}" },
      { label: "👤 Updated by", value: "{{updatedByName}}" },
    ])}<p>A new deadline is a fresh chance to deliver your best! 🌟</p>${cta("View on dashboard")}`,
  }),
  make({
    id: "task_priority_changed",
    name: "Task Priority Changed",
    category: "tasks",
    icon: "🔥",
    description: "Notify of changed task priority.",
    subject: "Task Priority Updated: {{taskTitle}}",
    eyebrow: "Heads up",
    title: "Priority changed for {{taskTitle}}",
    subtitle: "Adjust your focus accordingly.",
    accent: THEMES.amber,
    bodyHtml: `${dt([
      { label: "📋 Task", value: "{{taskTitle}}" },
      { label: "🔥 New Priority", value: "<strong>{{newPriority}}</strong>" },
      { label: "👤 Updated by", value: "{{updatedByName}}" },
    ])}<p>High priority, high impact — you've got this! 🔥🌟</p>${cta("View on dashboard", THEMES.amber)}`,
  }),
  make({
    id: "task_member_added",
    name: "Task Member Added",
    category: "tasks",
    icon: "👥",
    description: "New member added to an active task.",
    subject: "New Member Added to Your Task",
    eyebrow: "Team growing",
    title: "{{newMemberName}} joined {{taskTitle}}",
    subtitle: "A new member is now on this task.",
    bodyHtml: `${dt([
      { label: "📋 Task", value: "{{taskTitle}}" },
      { label: "👤 New Member", value: "{{newMemberName}}" },
      { label: "👤 Added by", value: "{{addedByName}}" },
      { label: "📅 Deadline", value: "{{dueDate}}" },
    ])}<p>Stronger team, better results! 💪🚀</p>${cta("View on dashboard")}`,
  }),

  // ----- LEAVES -----
  make({
    id: "leave_submitted",
    name: "Leave Submitted (Employee)",
    category: "leaves",
    icon: "📝",
    description: "Confirm leave request received.",
    subject: "Leave Request Submitted",
    eyebrow: "Awaiting approval",
    title: "Your leave request was submitted",
    subtitle: "Currently awaiting manager approval.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🏷 Leave Type", value: "{{leaveType}}" },
      { label: "📅 Leave Date", value: "{{leaveDate}}" },
      { label: "👤 Reporting to", value: "{{managerName}}" },
    ])}<p>You'll be notified once your manager reviews your request. In the meantime, feel free to reach out to HR if you have any questions. 🤝</p>`,
  }),
  make({
    id: "leave_request_manager",
    name: "Leave Request (Manager)",
    category: "leaves",
    icon: "📥",
    description: "New leave request requires manager approval.",
    subject: "Action Required: New Leave Request",
    eyebrow: "Pending your approval",
    title: "{{employeeName}} requested time off",
    subtitle: "A new leave request requires your approval.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Employee", value: "{{employeeName}}" },
      { label: "🏷 Type", value: "{{leaveType}}" },
      { label: "📅 Date", value: "{{leaveDate}}" },
      { label: "📝 Reason", value: "{{leaveReason}}" },
    ])}${cta("Review request", THEMES.amber)}<p>For any queries, contact HR at <a href="mailto:{{hrEmail}}">{{hrEmail}}</a>.</p>`,
  }),
  make({
    id: "leave_approved_sample",
    name: "Leave Approved",
    category: "leaves",
    icon: "✅",
    description: "Notify employee of approved leave.",
    subject: "✅ Your Leave Has Been Approved",
    eyebrow: "Approved by {{approvedBy}}",
    title: "Great news — your leave is approved",
    subtitle: "Recharge, refresh, come back stronger!",
    accent: THEMES.green,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Leave Date", value: "{{leaveDate}}" },
      { label: "🏷 Type", value: "{{leaveType}}" },
      { label: "👤 Approved by", value: "{{approvedBy}}" },
    ])}${cta("View leave details", THEMES.green)}<p>Have a wonderful day and keep up the great work! 🌟</p>`,
  }),
  make({
    id: "leave_rejected_sample",
    name: "Leave Rejected",
    category: "leaves",
    icon: "❌",
    description: "Notify employee of rejected leave.",
    subject: "Leave Request Not Approved ❌",
    eyebrow: "Reviewed by {{rejectedBy}}",
    title: "Your leave request wasn't approved",
    subtitle: "We regret to inform you that your leave wasn't approved.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🏷 Type", value: "{{leaveType}}" },
      { label: "📅 Date", value: "{{leaveDate}}" },
      { label: "👤 Reviewed by", value: "{{rejectedBy}}" },
      { label: "💬 Reason", value: "{{rejectionReason}}" },
    ])}<p>Please feel free to connect with your manager to discuss alternative dates or work something out together. 🤝</p>${cta("Open dashboard", THEMES.red)}`,
  }),
  make({
    id: "leave_team_member_today",
    name: "Team Member on Leave Today",
    category: "leaves",
    icon: "🌴",
    description: "Manager reminder: team member on leave today.",
    subject: "Leave Reminder",
    eyebrow: "Today's leave",
    title: "{{employeeName}} is on leave today",
    subtitle: "Plan your team's tasks accordingly.",
    accent: THEMES.teal,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Team Member", value: "{{employeeName}}" },
      { label: "📅 Leave Date", value: "{{leaveDate}}" },
      { label: "🏷 Leave Type", value: "{{leaveType}}" },
    ])}<p>If any urgent tasks need to be reassigned, kindly take the necessary action. 🤝</p>${cta("View team calendar", THEMES.teal)}`,
  }),
  make({
    id: "leave_pending_reminder",
    name: "Leave Pending Reminder (Manager)",
    category: "leaves",
    icon: "⏳",
    description: "Reminder to manager about pending approval.",
    subject: "Reminder — Leave Request Awaiting Your Approval",
    eyebrow: "Action needed",
    title: "A leave request is awaiting your approval",
    subtitle: "Kindly review at the earliest.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Employee", value: "{{employeeName}}" },
      { label: "🏷 Leave Type", value: "{{leaveType}}" },
      { label: "📅 Leave Date", value: "{{leaveDate}}" },
    ])}${cta("Review leave request", THEMES.amber)}<p>The employee can plan accordingly once you take action. 🤝</p>`,
  }),

  make({
    id: "leave_team_on_leave_rollup_manager",
    name: "Team On Leave Today — Roll-up (Manager)",
    category: "leaves",
    icon: "🌴",
    description:
      "Manager roll-up listing all direct reports on approved leave today.",
    subject: "🌴 {{totalCount}} of your team are on leave today",
    eyebrow: "Today's leave",
    title: "{{totalCount}} team member(s) on leave today",
    subtitle: "Plan your team's tasks accordingly.",
    accent: THEMES.teal,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports are on approved leave today.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "👥 On Leave", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Who's out today</p>{{teamRowsHtml}}<p>If any urgent tasks need to be reassigned, kindly take the necessary action. 🤝</p>${cta("View team calendar", THEMES.teal)}`,
  }),
  make({
    id: "leave_pending_rollup_manager",
    name: "Pending Leave Approvals — Roll-up (Manager)",
    category: "leaves",
    icon: "⏳",
    description:
      "Manager roll-up of all direct reports' leave requests still awaiting approval.",
    subject: "⏳ {{totalCount}} leave request(s) awaiting your approval",
    eyebrow: "Action needed",
    title: "{{totalCount}} pending leave request(s)",
    subtitle: "Kindly review at the earliest.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> leave request(s) from your team are still pending your approval.</p>",
    bodyHtml: `${dt([
      { label: "📅 As of", value: "{{todaysDate}}" },
      { label: "⏳ Pending", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Awaiting approval</p>{{teamRowsHtml}}<p>Please take action at the earliest so your team can plan accordingly. 🤝</p>${cta("Review leave requests", THEMES.amber)}`,
  }),

  // ----- ATTENDANCE -----
  make({
    id: "attendance_absent_employee",
    name: "Absent (Employee)",
    category: "attendance",
    icon: "🚫",
    description: "Employee marked absent for the day.",
    subject: "You have been marked absent for {{attendance_date}}",
    eyebrow: "Attendance",
    title: "We missed you today!",
    subtitle: "Hope you're doing well. 💛",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{attendance_date}}" },
      {
        label: "📌 Status",
        value: "<span style='color:#dc2626;font-weight:600;'>Absent</span>",
      },
    ])}<p>If you need anything or want to share a reason, please reach out to your manager or HR. We're here for you! 🤝</p>${cta("View attendance", THEMES.red)}`,
  }),
  make({
    id: "attendance_absent_manager",
    name: "Absent (Manager)",
    category: "attendance",
    icon: "👀",
    description: "Manager alert for team member's absence.",
    subject: "Attendance Alert — {{employeeName}}",
    eyebrow: "Team attendance",
    title: "{{employeeName}} is absent today",
    subtitle: "Take action if required.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Employee", value: "{{employeeName}}" },
      { label: "📅 Date", value: "{{attendance_date}}" },
      {
        label: "📌 Status",
        value: "<span style='color:#dc2626;font-weight:600;'>Absent</span>",
      },
    ])}<p>Connect with the employee if needed.</p>${cta("View attendance", THEMES.red)}`,
  }),
  make({
    id: "attendance_late_sample",
    name: "Late Arrival",
    category: "attendance",
    icon: "⏰",
    description: "Friendly nudge for late arrival.",
    subject: "Late Arrival on {{attendance_date}}",
    eyebrow: "Attendance",
    title: "Late arrival noted",
    subtitle: "Hope you're doing well! 😊",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `<p>We noticed you checked in <strong>{{lateBy}}</strong> late today at {{check_in_time}}. No worries — we all have such days!</p>${dt(
      [
        { label: "📅 Date", value: "{{attendance_date}}" },
        { label: "⏰ Check-in", value: "{{check_in_time}}" },
        { label: "⏱️ Late by", value: "{{lateBy}}" },
      ],
    )}<p>Just a small reminder to try and be on time going forward. Take care and see you on time tomorrow! 💪</p>`,
  }),
  make({
    id: "attendance_late_repeated",
    name: "Repeated Late Arrivals",
    category: "attendance",
    icon: "🚨",
    description: "Warning for 4+ late check-ins in a month.",
    subject: "Reminder: Late Check-Ins This Month",
    eyebrow: "Pattern noticed",
    title: "You've had {{lateCount}} late check-ins this month",
    subtitle: "Let's get back on track together.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `<p>Please note you are currently under observation, and continued late arrivals could affect your pay going forward.</p>${dt(
      [
        { label: "📅 Month", value: "{{currentMonth}}" },
        { label: "⏰ Late check-ins", value: "<strong>{{lateCount}}</strong>" },
      ],
    )}<p>We're confident you can turn this around. If there's anything making it difficult to check in on time, feel free to reach out — we're here to help.</p>`,
  }),
  make({
    id: "attendance_no_checkin",
    name: "No Check-in Reminder",
    category: "attendance",
    icon: "📍",
    description: "Reminder to mark attendance when check-in missing.",
    subject: "Attendance Check-in Reminder",
    eyebrow: "Friendly reminder",
    title: "Looks like you haven't checked in yet",
    subtitle: "Hope you're having a great start to your day! 😊",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "⏰ Expected Check-in", value: "{{expectedCheckin}}" },
      { label: "📌 Status", value: "Not checked in" },
    ])}<p>If you're already at your desk, you may have simply forgotten to mark your attendance. Please log in and check in as soon as possible to keep your attendance record accurate. ✅</p>${cta("Mark attendance", THEMES.amber)}`,
  }),
  make({
    id: "attendance_early_checkin_sample",
    name: "Early Check-in (Early Bird)",
    category: "attendance",
    icon: "☀️",
    description: "Appreciate punctual employees.",
    subject: "Early Check-in ☀️🌟",
    eyebrow: "Attendance · Early Bird",
    title: "What a wonderful way to start the day",
    subtitle: "You checked in early today — we appreciate it!",
    accent: THEMES.green,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{attendance_date}}" },
      { label: "⏰ Check-in", value: "{{check_in_time}}" },
      {
        label: "🏆 Status",
        value:
          "<span style='color:#16a34a;font-weight:600;'>Early Bird!</span>",
      },
    ])}<p>Starting your day ahead of time reflects your dedication and passion — it never goes unnoticed! 💛 Keep up this fantastic habit!</p>`,
  }),
  make({
    id: "attendance_early_checkout_sample",
    name: "Early Checkout",
    category: "attendance",
    icon: "🚪",
    description: "Flag unapproved early checkouts.",
    subject: "Early Checkout on {{attendance_date}}",
    eyebrow: "Attendance · Early Checkout",
    title: "Early checkout noted",
    subtitle: "You checked out before your shift end today.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{attendance_date}}" },
      { label: "🚪 Check-out", value: "{{check_out_time}}" },
      { label: "⏰ Shift end", value: "{{shiftEnd}}" },
      { label: "⏱️ Early by", value: "{{earlyBy}}" },
    ])}<p>If you had a valid reason, please inform your manager or HR so it can be recorded properly. Repeated unapproved early checkouts may be flagged.</p>`,
  }),
  make({
    id: "attendance_no_checkout",
    name: "No Checkout Reminder",
    category: "attendance",
    icon: "📌",
    description: "Reminder for missing checkout record.",
    subject: "Checkout Reminder — Action Required",
    eyebrow: "Attendance",
    title: "Looks like you forgot to check out",
    subtitle: "Please reach out to HR to update your checkout time.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{attendance_date}}" },
      { label: "📌 Status", value: "No Checkout Recorded" },
    ])}<p>If you're facing any issues with checkout, feel free to reach out to HR right away — we're here to help! 🤝💛</p>${cta("Mark your checkout", THEMES.amber)}`,
  }),
  make({
    id: "attendance_short_leave",
    name: "Short Leave Marked",
    category: "attendance",
    icon: "🕐",
    description: "Inform about short-leave attendance status.",
    subject: "Short Leave Marked",
    eyebrow: "Attendance",
    title: "Today marked as Short Leave",
    subtitle: "Recorded on your attendance.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{attendance_date}}" },
      {
        label: "📌 Status",
        value:
          "<span style='color:#b45309;font-weight:600;'>Short Day Leave</span>",
      },
    ])}<p>If you feel this has been marked incorrectly, please reach out to your manager or HR. 🤝</p>${cta("View attendance", THEMES.amber)}`,
  }),

  make({
    id: "attendance_no_checkin_manager",
    name: "No Check-in — Roll-up (Manager)",
    category: "attendance",
    icon: "📍",
    description:
      "Manager roll-up of direct reports who haven't checked in today.",
    subject: "📍 {{totalCount}} of your team haven't checked in",
    eyebrow: "Team attendance",
    title: "{{totalCount}} team member(s) not checked in",
    subtitle: "As of this morning's check.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports haven't checked in yet today and aren't on approved leave.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "👥 Not Checked In", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Pending check-in</p>{{teamRowsHtml}}<p>Please follow up with your team so their attendance stays accurate. ✅</p>${cta("View team attendance", THEMES.amber)}`,
  }),
  make({
    id: "attendance_no_checkout_manager",
    name: "No Checkout — Roll-up (Manager)",
    category: "attendance",
    icon: "📌",
    description:
      "Manager roll-up of direct reports who checked in but never checked out today.",
    subject: "📌 {{totalCount}} of your team forgot to check out",
    eyebrow: "Team attendance",
    title: "{{totalCount}} missing checkout(s)",
    subtitle: "End-of-day attendance review.",
    accent: THEMES.violet,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports checked in today but haven't recorded a checkout.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "👥 No Checkout", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Pending checkout</p>{{teamRowsHtml}}<p>A quick nudge will help keep their attendance records accurate. 🤝</p>${cta("View team attendance", THEMES.violet)}`,
  }),
  make({
    id: "attendance_late_manager",
    name: "Late Arrivals — Roll-up (Manager)",
    category: "attendance",
    icon: "⏰",
    description: "Manager roll-up of direct reports who arrived late today.",
    subject: "⏰ {{totalCount}} of your team arrived late today",
    eyebrow: "Team attendance",
    title: "{{totalCount}} late arrival(s) today",
    subtitle: "Today's punctuality snapshot.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports checked in after the shift start today.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "⏰ Late Arrivals", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Late check-ins</p>{{teamRowsHtml}}<p>A quick word with the team helps everyone start on time. 💪</p>${cta("View team attendance", THEMES.amber)}`,
  }),
  make({
    id: "attendance_late_repeated_manager",
    name: "Repeated Late Arrivals — Roll-up (Manager)",
    category: "attendance",
    icon: "🚨",
    description:
      "Manager roll-up of direct reports who crossed the late-arrival limit this month.",
    subject: "🚨 {{totalCount}} of your team are repeatedly late this month",
    eyebrow: "Pattern noticed",
    title: "{{totalCount}} team member(s) over the late limit",
    subtitle: "Monthly late-arrival watchlist.",
    accent: THEMES.red,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports have crossed the late check-in limit for <strong>{{currentMonth}}</strong>.</p>",
    bodyHtml: `${dt([
      { label: "📅 Month", value: "{{currentMonth}}" },
      { label: "🚨 Over Limit", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Late count by team member</p>{{teamRowsHtml}}<p>Consider checking in with them to understand and resolve what's getting in the way.</p>${cta("View team attendance", THEMES.red)}`,
  }),
  make({
    id: "attendance_short_leave_manager",
    name: "Short Leave Today — Roll-up (Manager)",
    category: "attendance",
    icon: "🕐",
    description:
      "Manager roll-up of direct reports on short leave today, by status.",
    subject: "🕐 {{totalCount}} of your team are on short leave today",
    eyebrow: "Today's short leave",
    title: "{{totalCount}} short leave(s) today",
    subtitle: "Plan the day accordingly.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports have a short leave covering today.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "🕐 Short Leaves", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">On short leave today</p>{{teamRowsHtml}}<p>Adjust task timing where needed so the day runs smoothly. 🤝</p>${cta("View team attendance", THEMES.amber)}`,
  }),

  // ----- LEAD ACTIVITY -----
  make({
    id: "lead_new_assigned",
    name: "New Leads Assigned (Counselor)",
    category: "leads",
    icon: "🎯",
    description: "Counselor receives newly assigned leads from manager.",
    subject: "🌟 {{leadCount}} New Leads Assigned to You – Action Required!",
    eyebrow: "Leads",
    title: "{{leadCount}} new lead(s) assigned to you",
    subtitle: "Every great deal starts with one conversation!",
    accent: THEMES.red,
    greeting:
      "<p>Hey <strong>{{counselorName}}</strong>!</p><p><strong>{{leadCount}}</strong> new lead(s) have just been assigned to you!</p>",
    bodyHtml: `${dt([
      { label: "👤 Assigned By", value: "{{managerName}}" },
      { label: "📅 Assigned On", value: "{{assignedDate}}" },
      { label: "🕒 Time", value: "{{assignedTime}}" },
      {
        label: "📊 Total Leads Assigned",
        value: "<strong>{{leadCount}}</strong>",
      },
      { label: "📌 Pipeline Stage", value: "{{stageName}}" },
    ])}<p>Please log in to your dashboard to review all assigned leads and begin actioning them as soon as possible.</p><p>Every great deal starts with one conversation!</p>${cta("👉 View Your Leads", THEMES.red)}`,
  }),
  make({
    id: "lead_won_counselor",
    name: "Lead Won (Counselor)",
    category: "leads",
    icon: "🏆",
    description: "Celebrate a closed deal with the counselor.",
    subject: "🏆 Woohoo! You Just Closed a Lead",
    eyebrow: "Lead won",
    title: "YOU DID IT! 🏆🔥",
    subtitle: "Your hard work just turned into a Win.",
    accent: THEMES.green,
    greeting:
      "<p>Hey <strong>{{counselorName}}</strong>! 🎉</p><p>Your lead <strong>{{leadName}}</strong> has been successfully closed and we couldn't be prouder!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "📅 Date", value: "{{closeDate}}" },
      {
        label: "✅ Status",
        value: "<strong style='color:#16a34a;'>{{currentPipeline}}</strong>",
      },
    ])}<p>This is what dedication and hard work looks like. Keep shining, champ — the best is yet to come! ⭐</p>${cta("👉 View Lead on Dashboard", THEMES.green)}`,
  }),
  make({
    id: "lead_won_manager",
    name: "Lead Won (Manager)",
    category: "leads",
    icon: "🎉",
    description: "Manager celebrates team win.",
    subject: "🎉 Win Alert! {{counselorName}} just closed {{leadName}}",
    eyebrow: "Team win",
    title: "Your team just scored a WIN! 🏆🔥",
    subtitle: "Celebrate the closure with your team.",
    accent: THEMES.green,
    greeting:
      "<p>Hey <strong>{{managerName}}</strong>! 🎉</p><p>Celebration time — your team just scored a WIN!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "👨‍💼 Counselor", value: "{{counselorName}}" },
      { label: "📅 Date", value: "{{closeDate}}" },
      {
        label: "✅ Status",
        value: "<strong style='color:#16a34a;'>{{currentPipeline}}</strong>",
      },
    ])}<p>Your team is absolutely crushing it! This is a reflection of great leadership and an amazing team. Keep the wins coming!</p>${cta("👉 View Lead on Dashboard", THEMES.green)}`,
  }),
  make({
    id: "lead_new_added_manager",
    name: "New Lead Added (Manager)",
    category: "leads",
    icon: "📥",
    description:
      "Manager is notified when a counselor adds a new lead to the pipeline.",
    subject: "New Deal Added to Pipeline",
    eyebrow: "Pipeline activity",
    title: "New lead added by {{counselorName}}",
    subtitle: "Stay on top of your team's pipeline.",
    accent: THEMES.indigo,
    greeting:
      "<p>Hey <strong>{{managerName}}</strong>!</p><p>A new lead has just been added to the pipeline by <strong>{{counselorName}}</strong>.</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "👤 Counselor", value: "{{counselorName}}" },
      { label: "📞 Contact", value: "{{leadPhone}}" },
      { label: "📧 Email", value: "{{leadEmail}}" },
      { label: "📅 Date Added", value: "{{addedDate}}" },
    ])}<p>Stay on top of your team's pipeline and keep the wins rolling in!</p>${cta("🔗 View Deal on Dashboard", THEMES.indigo)}`,
  }),
  make({
    id: "lead_status_changed_counselor",
    name: "Lead Status Changed (Counselor)",
    category: "leads",
    icon: "🚀",
    description:
      "Counselor is notified when a lead moves to a new pipeline stage.",
    subject: "🚀 Lead Status Updated – {{leadName}}",
    eyebrow: "Status change",
    title: "{{leadName}} just moved forward",
    subtitle: "Every step forward is a step closer to the Win.",
    accent: THEMES.blue,
    greeting:
      "<p>Hey <strong>{{counselorName}}</strong>!</p><p>Your lead just moved forward — keep up the momentum!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "📅 Date", value: "{{changeDate}}" },
      {
        label: "🔄 Status",
        value: "{{oldStatus}} ➡️ <strong>{{newStatus}}</strong>",
      },
    ])}<p>Every step forward is a step closer to the Win. You're doing amazing — keep going!</p>${cta("👉 View Lead on Dashboard", THEMES.blue)}`,
  }),
  make({
    id: "lead_status_changed_manager",
    name: "Lead Status Changed (Manager)",
    category: "leads",
    icon: "📈",
    description:
      "Manager is notified when a lead moves to a new pipeline stage.",
    subject: "📈 Lead Status Update – {{leadName}} | {{counselorName}}",
    eyebrow: "Team progress",
    title: "{{leadName}} moved to {{newStatus}}",
    subtitle: "Your team is crushing it.",
    accent: THEMES.blue,
    greeting:
      "<p>Hey <strong>{{managerName}}</strong>!</p><p>Great progress — one of your team's leads just moved to a new status!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "👨‍💼 Counselor", value: "{{counselorName}}" },
      { label: "📅 Date", value: "{{changeDate}}" },
      {
        label: "🔄 Status",
        value: "{{oldStatus}} ➡️ <strong>{{newStatus}}</strong>",
      },
    ])}<p>Your team is crushing it! Keep the energy going!</p>${cta("👉 View Lead on Dashboard", THEMES.blue)}`,
  }),
  make({
    id: "lead_lost_counselor",
    name: "Lead Lost (Counselor)",
    category: "leads",
    icon: "💛",
    description: "Counselor is encouraged after a lost lead.",
    subject: "💛 It's Okay — Your Next Win is Just Around the Corner!",
    eyebrow: "Keep going",
    title: "This one didn't go our way",
    subtitle: "Every setback is a setup for a bigger comeback.",
    accent: THEMES.amber,
    greeting:
      "<p>Hey <strong>{{counselorName}}</strong>!</p><p>This one didn't go our way, but that's okay — every setback is a setup for a bigger comeback!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "📅 Date", value: "{{closeDate}}" },
      {
        label: "📌 Status",
        value: "<strong style='color:#dc2626;'>{{currentPipeline}}</strong>",
      },
    ])}<p>Take a moment, reflect, and come back stronger. The next big Win has your name on it!</p>${cta("👉 View Lead on Dashboard", THEMES.amber)}`,
  }),
  make({
    id: "lead_lost_manager",
    name: "Lead Lost (Manager)",
    category: "leads",
    icon: "💛",
    description: "Manager is updated when one of the team's leads is lost.",
    subject: "💛 Lead Update – {{leadName}} | Keep the Team Motivated!",
    eyebrow: "Lead update",
    title: "{{leadName}} marked as Lost",
    subtitle: "Every loss is a lesson in disguise.",
    accent: THEMES.amber,
    greeting:
      "<p>Hey <strong>{{managerName}}</strong>!</p><p>One lead didn't close this time, but your team is still going strong!</p>",
    bodyHtml: `${dt([
      { label: "👤 Lead Name", value: "{{leadName}}" },
      { label: "👨‍💼 Counselor", value: "{{counselorName}}" },
      { label: "📅 Date", value: "{{closeDate}}" },
      {
        label: "📌 Status",
        value: "<strong style='color:#dc2626;'>{{currentPipeline}}</strong>",
      },
    ])}<p>Every loss is a lesson in disguise. Keep encouraging the team — bigger wins are on the way!</p>${cta("👉 View Lead on Dashboard", THEMES.amber)}`,
  }),
  make({
    id: "lead_transferred",
    name: "Lead Transferred (Counselor)",
    category: "leads",
    icon: "🔄",
    description: "Lead transferred to a new counselor.",
    subject: "🔄 Lead(s) Transferred to You — Action Required!",
    eyebrow: "Transferred",
    title: "{{leadCount}} lead(s) transferred to you",
    subtitle: "Action required at your earliest.",
    accent: THEMES.blue,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Transferred by", value: "{{transferredBy}}" },
      { label: "👤 From", value: "{{previousCounselor}}" },
      { label: "📊 Total", value: "<strong>{{leadCount}}</strong>" },
      { label: "📌 Stage", value: "{{stageName}}" },
    ])}${cta("View your leads", THEMES.blue)}`,
  }),
  make({
    id: "lead_transferred_manager",
    name: "Lead Transferred (Manager)",
    category: "leads",
    icon: "🔄",
    description: "Lead(s) transferred between counselors — confirmation to the manager.",
    subject: "🔄 Lead Transfer Confirmed — Lead(s) Transferred to {{newCounselor}}",
    eyebrow: "Transferred",
    title: "Lead transfer completed",
    subtitle: "{{leadCount}} lead(s) moved within your team.",
    accent: THEMES.blue,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Transferred From", value: "{{previousCounselor}}" },
      { label: "👤 Transferred To", value: "{{newCounselor}}" },
      { label: "👤 Transferred By", value: "{{transferredBy}}" },
      { label: "📊 Total Lead(s)", value: "<strong>{{leadCount}}</strong>" },
      { label: "📌 Pipeline Stage", value: "{{stageName}}" },
      { label: "📅 Date", value: "{{transferDate}}" },
    ])}${callout(
      "The leads are now visible in {{newCounselor}}'s pipeline and they have been notified accordingly.",
      THEMES.blue,
    )}${cta("View Full Dashboard", THEMES.blue)}`,
  }),
  make({
    id: "lead_unassigned_alert",
    name: "Unassigned Leads Detected (Manager)",
    category: "leads",
    icon: "⚠️",
    description:
      "Alert manager that new leads have not been assigned to a counselor.",
    subject:
      "⚠️ {{leadCount}} Unassigned Lead(s) Detected – Immediate Action Required!",
    eyebrow: "Action needed",
    title: "{{leadCount}} unassigned lead(s)",
    subtitle: "Don't let these opportunities slip!",
    accent: THEMES.red,
    greeting:
      "<p>Hey <strong>{{managerName}}</strong>!</p><p><strong>{{leadCount}}</strong> lead(s) have been added to the system but have not been assigned to any Counselor yet.</p>",
    bodyHtml: `${dt([
      { label: "📅 Leads Detected", value: "{{detectedDate}}" },
      {
        label: "📊 Total Unassigned Lead(s)",
        value: "<strong>{{leadCount}}</strong>",
      },
      { label: "📍 Sources", value: "{{leadSources}}" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Unassigned leads</p>{{unassignedRowsHtml}}<p>Please log in and assign these leads to your Counselors as soon as possible to ensure no opportunity is missed!</p>${cta("🔗 Assign Leads on Dashboard", THEMES.red)}`,
  }),
  make({
    id: "manager_inactive_counsellors",
    name: "Inactive Counsellors Roll-up (Manager)",
    category: "leads",
    icon: "💤",
    description:
      "Roll-up alert listing the manager's counsellors who haven't logged a call recently.",
    subject: "💤 {{idleCount}} of your counsellor(s) are inactive",
    eyebrow: "Team activity",
    title: "{{idleCount}} idle counsellor(s)",
    subtitle: "No calls logged recently",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{idleCount}}</strong> counsellor(s) on your team haven't logged a call recently.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "💤 Idle Counsellors", value: "<strong>{{idleCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Idle counsellors</p>{{inactiveRowsHtml}}<p>Please check in with your team to keep the pipeline moving.</p>${cta("📊 View Team Activity", THEMES.amber)}`,
  }),
  make({
    id: "manager_team_performance",
    name: "Team Performance Summary (Manager)",
    category: "leads",
    icon: "📊",
    description:
      "Send a manager their team's call + conversion summary for the period, with a per-counsellor breakdown.",
    subject: "📊 Team Performance — {{periodLabel}}",
    eyebrow: "Performance",
    title: "Team summary · {{periodLabel}}",
    subtitle: "{{dateRange}}",
    accent: THEMES.violet,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p>Here's how your team performed during <strong>{{dateRange}}</strong>.</p>",
    bodyHtml: `${dt([
      { label: "🗓️ Period", value: "{{periodLabel}} · {{dateRange}}" },
      { label: "👥 Team Size", value: "{{teamSize}}" },
      { label: "📞 Total Calls", value: "<strong>{{totalCalls}}</strong>" },
      { label: "✅ Registrations", value: "{{registrations}}" },
      { label: "❌ Lost", value: "{{lost}}" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Team breakdown</p>{{summaryRowsHtml}}<p>Great work keeping the team on track — reach out to anyone who needs support.</p>${cta("📈 View Detailed Report", THEMES.violet)}`,
  }),
  make({
    id: "counsellor_performance_summary",
    name: "Performance Summary (Counsellor)",
    category: "leads",
    icon: "📊",
    description:
      "Send each counsellor their own call + lead summary for the period.",
    subject: "📊 Your {{periodLabel}} Performance Summary",
    eyebrow: "Performance",
    title: "Your summary · {{periodLabel}}",
    subtitle: "{{dateRange}}",
    accent: THEMES.indigo,
    greeting:
      "<p>Hi <strong>{{recipientName}}</strong>,</p><p>Here's how you did during <strong>{{dateRange}}</strong>. Keep it up! 🚀</p>",
    bodyHtml: `${dt([
      { label: "🗓️ Period", value: "{{periodLabel}} · {{dateRange}}" },
      { label: "📞 Total Calls", value: "<strong>{{totalCalls}}</strong>" },
      { label: "⏱️ Talk Time", value: "{{totalHours}}" },
      { label: "📵 Missed Calls", value: "{{missedCalls}}" },
      { label: "🎯 Leads Handled", value: "{{totalLeads}}" },
      { label: "✅ Registrations", value: "{{registrations}}" },
      { label: "🔄 In Process", value: "{{inProcess}}" },
      { label: "❌ Lost", value: "{{lost}}" },
    ])}<p>Review your pipeline and follow up with leads still in process to close more this period.</p>${cta("📈 Open My Dashboard", THEMES.indigo)}`,
  }),
  make({
    id: "lead_followup_due",
    name: "Active Follow-ups Due Today",
    category: "leads",
    icon: "📞",
    description: "Counselor reminder about pending follow-ups.",
    subject: "⏰ Active Follow-up Due Today",
    eyebrow: "Reminders",
    title: "You have {{reminderCount}} reminders today",
    subtitle: "One call can change everything.",
    accent: THEMES.blue,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `<p>Just a friendly reminder — you have <strong>{{reminderCount}}</strong> new reminders due today. Don't let this opportunity slip! 🔥</p>${dt(
      [
        { label: "📅 Date", value: "{{todayDate}}" },
        { label: "📊 Reminders", value: "<strong>{{reminderCount}}</strong>" },
      ],
    )}${cta("View leads on dashboard", THEMES.blue)}`,
  }),
  make({
    id: "lead_no_activity",
    name: "No Activity from Counselor",
    category: "leads",
    icon: "💤",
    description: "Counselor logged in but inactive 30+ mins.",
    subject: "👋 We Miss You! No Activity Detected",
    eyebrow: "Inactivity",
    title: "Quiet for the past 30 minutes",
    subtitle: "Taking a snack break, are we? 🍪😄",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🕒 Last Activity At", value: "{{lastActivity}}" },
      { label: "📅 Date", value: "{{todayDate}}" },
    ])}<p>If you're on a quick break, no worries — just make sure to log back in and stay on top of your leads. If you're experiencing technical issues, please reach out to your manager immediately.</p>${cta("Go to dashboard", THEMES.amber)}`,
  }),
  make({
    id: "lead_tasks_due_tomorrow",
    name: "Tasks Due Tomorrow",
    category: "leads",
    icon: "⏰",
    description: "Daily reminder of tasks due tomorrow with a task list.",
    subject: "⏰ Reminder: You Have Tasks Due Tomorrow",
    eyebrow: "Heads up",
    title: "You have tasks due tomorrow",
    subtitle: "Please complete or update before the deadline.",
    accent: THEMES.amber,
    greeting: `<p>Hi <strong>{{userName}}</strong>!</p><p>Tasks due tomorrow ({{dueDateLabel}}) — please complete or update before the deadline.</p>`,
    bodyHtml: `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#ffffff;">{{taskListHtml}}</div><p>Please update the status once completed. Reach out to your manager if you need any support.</p>`,
  }),
  make({
    id: "reminders_summary",
    name: "Your Reminders Summary",
    category: "leads",
    icon: "🔔",
    description:
      "Per-assignee follow-up reminders digest. Pair with Fetch Leads Reminders + per-recipient Send Email.",
    subject: "Your reminders summary",
    eyebrow: "Daily summary",
    title: "Your Reminders Summary",
    subtitle: "Here's the snapshot of reminders that need your attention.",
    greeting: "<p>Hi <strong>{{recipientName}}</strong>,</p>",
    bodyHtml: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:12px 0;">
<tr><td style="padding:12px 0;width:40%;font:500 12px/18px 'Inter',Arial,sans-serif;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;border-bottom:1px solid #e2e8f0;">📋 Total</td><td align="right" style="padding:12px 0;font:500 13px/20px 'Inter',Arial,sans-serif;color:#0f172a;border-bottom:1px solid #e2e8f0;">{{totalCount}}</td></tr>
<tr><td style="padding:12px 0;width:40%;font:500 12px/18px 'Inter',Arial,sans-serif;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;border-bottom:1px solid #e2e8f0;">⚠️ Overdue</td><td align="right" style="padding:12px 0;font:500 13px/20px 'Inter',Arial,sans-serif;color:#0f172a;border-bottom:1px solid #e2e8f0;"><span style="color:#dc2626;font-weight:600;">{{overdueCount}}</span></td></tr>
<tr><td style="padding:12px 0;width:40%;font:500 12px/18px 'Inter',Arial,sans-serif;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;border-bottom:1px solid #e2e8f0;">📅 Today</td><td align="right" style="padding:12px 0;font:500 13px/20px 'Inter',Arial,sans-serif;color:#0f172a;border-bottom:1px solid #e2e8f0;">{{todayCount}}</td></tr>
<tr><td style="padding:12px 0;width:40%;font:500 12px/18px 'Inter',Arial,sans-serif;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;">📆 Upcoming</td><td align="right" style="padding:12px 0;font:500 13px/20px 'Inter',Arial,sans-serif;color:#0f172a;">{{upcomingCount}}</td></tr>
</table>
<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#ffffff;margin:12px 0;">{{reminderRowsHtml}}</div>
<p style="text-align:center;margin:24px 0;"><a href="{{dashboardLink}}" style="display:inline-block;padding:13px 30px;background:#3730a3;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;font-size:14px;letter-spacing:0.02em;">Open dashboard</a></p>`,
  }),

  // ----- REPORTS -----
  make({
    id: "report_daily_summary",
    name: "Daily Summary (Counselor)",
    category: "reports",
    icon: "📊",
    description: "End-of-day performance recap.",
    subject: "📊 Your Daily Summary — {{reportDate}}",
    eyebrow: "Daily summary",
    title: "Your activity today",
    subtitle: "Here's the complete breakdown.",
    accent: THEMES.violet,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `<p>📞 CALLS</p>${dt([
      { label: "Total Calls Done", value: "{{totalCalls}}" },
      { label: "Hours Engaged", value: "{{hoursEngaged}}" },
    ])}<p>📌 OVERALL DEAL SUMMARY</p>${dt([
      { label: "Added in Moderate", value: "{{moderateCount}}" },
      { label: "Active Follow-up", value: "{{followupCount}}" },
      { label: "Documentation Received", value: "{{docsCount}}" },
      {
        label: "Registration Done",
        value: "<strong style='color:#16a34a;'>{{registrationCount}}</strong>",
      },
      { label: "Lost", value: "{{lostCount}}" },
    ])}${cta("View full dashboard", THEMES.violet)}`,
  }),
  make({
    id: "report_weekly_summary",
    name: "Weekly Summary",
    category: "reports",
    icon: "📈",
    description: "Weekly performance recap with comparison.",
    subject: "📊 Your Weekly Summary — {{weekRange}}",
    eyebrow: "Weekly summary",
    title: "Your week at a glance",
    subtitle: "Here's how the week went.",
    accent: THEMES.violet,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `<p>📞 CALLS</p>${dt([
      { label: "Total Calls", value: "{{totalCalls}}" },
      { label: "Hours Engaged", value: "{{hoursEngaged}}" },
    ])}<p>📈 WEEK-ON-WEEK</p>${dt([
      {
        label: "Calls Done",
        value: "{{prevCalls}} → <strong>{{totalCalls}}</strong>",
      },
      {
        label: "Registrations",
        value: "{{prevReg}} → <strong>{{regCount}}</strong>",
      },
      {
        label: "Conversion Rate",
        value: "{{prevConv}} → <strong>{{convRate}}</strong>",
      },
    ])}${cta("View full dashboard", THEMES.violet)}<p>Have a great weekend — come back stronger next week! 💪🏆</p>`,
  }),
  make({
    id: "report_monthly_summary",
    name: "Monthly Summary",
    category: "reports",
    icon: "📋",
    description: "Monthly performance recap.",
    subject: "📊 Your Monthly Summary — {{monthName}}",
    eyebrow: "Monthly summary",
    title: "Your month in review",
    subtitle: "The full breakdown of your month.",
    accent: THEMES.violet,
    greeting: "<p>Hi <strong>{{counselorName}}</strong>,</p>",
    bodyHtml: `<p>📊 OVERALL</p>${dt([
      { label: "Total Calls", value: "{{totalCalls}}" },
      {
        label: "Registrations",
        value: "<strong style='color:#16a34a;'>{{regCount}}</strong>",
      },
      { label: "Conversion Rate", value: "{{convRate}}" },
    ])}${cta("View full dashboard", THEMES.violet)}<p>Have a great month ahead — let's make it even better! 💪🏆</p>`,
  }),
  make({
    id: "report_overdue_employee",
    name: "Report Overdue (Employee)",
    category: "reports",
    icon: "📋",
    description: "Reminder to submit overdue report.",
    subject: "Friendly reminder: your report is overdue",
    eyebrow: "Action needed",
    title: "Your {{reportTitle}} is overdue",
    subtitle: "Please submit as soon as you can.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `<p>We noticed your <strong>{{reportTitle}}</strong> hasn't been submitted yet and the due date has passed.</p>${dt(
      [
        { label: "📋 Report", value: "{{reportTitle}}" },
        { label: "📅 Due Date", value: "{{dueDate}}" },
      ],
    )}<p>No worries — it happens! Please submit it as soon as you can so your team stays on track.</p>${cta("Submit your report", THEMES.red)}`,
  }),
  make({
    id: "report_overdue_manager",
    name: "Report Overdue (Manager)",
    category: "reports",
    icon: "👀",
    description: "Alert manager of employee's overdue report.",
    subject: "Action needed: {{employeeName}}'s report is overdue",
    eyebrow: "Heads up",
    title: "{{employeeName}}'s report is overdue",
    subtitle: "Worth checking in with them.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `<p>Just a quick heads-up — <strong>{{employeeName}}</strong>'s {{reportTitle}} was due on {{dueDate}} and hasn't come in yet.</p>${dt(
      [
        { label: "👤 Employee", value: "{{employeeName}}" },
        { label: "📋 Report", value: "{{reportTitle}}" },
        { label: "📅 Due Date", value: "{{dueDate}}" },
      ],
    )}${cta("View reports", THEMES.amber)}`,
  }),
  make({
    id: "report_new_submission",
    name: "New Report Submission",
    category: "reports",
    icon: "📝",
    description: "Manager notified of new report submission.",
    subject: "📝 New submission: {{reportTitle}}",
    eyebrow: "New submission",
    title: "{{employeeName}} submitted a report",
    subtitle: "Ready for your review.",
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "👤 Employee", value: "{{employeeName}}" },
      { label: "📋 Report", value: "{{reportTitle}}" },
      { label: "📅 Submitted", value: "{{submittedAt}}" },
    ])}${cta("View report")}`,
  }),

  make({
    id: "report_overdue_rollup_manager",
    name: "Report Overdue — Roll-up (Manager)",
    category: "reports",
    icon: "🚨",
    description:
      "Manager roll-up of all direct reports' overdue (daily/weekly/monthly) reports.",
    subject: "🚨 {{totalCount}} overdue report(s) across your team",
    eyebrow: "Action needed",
    title: "{{totalCount}} overdue report(s)",
    subtitle: "Your team's missed submissions.",
    accent: THEMES.red,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> report(s) from your direct reports are past their due date and still not submitted.</p>",
    bodyHtml: `${dt([
      { label: "📅 As of", value: "{{todaysDate}}" },
      { label: "🚨 Overdue", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Overdue reports by team member</p>{{teamRowsHtml}}<p>It might be worth checking in with them to see if they need support or an extension.</p>${cta("View reports", THEMES.red)}`,
  }),
  make({
    id: "report_reminder_employee",
    name: "Report Reminder (Employee)",
    category: "reports",
    icon: "⏰",
    description:
      "Reminder to an employee that their daily/weekly/monthly report is due today.",
    subject: "⏰ Reminder: your {{frequency}} report is due today",
    eyebrow: "Friendly reminder",
    title: "Your {{reportTitle}} is due today",
    subtitle: "Take a couple of minutes and get it submitted. 🙌",
    accent: THEMES.violet,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `<p>Just a quick nudge — your <strong>{{frequency}}</strong> report is due today and hasn't been submitted yet.</p>${dt(
      [
        { label: "📋 Report", value: "{{reportTitle}}" },
        { label: "🔁 Frequency", value: "{{frequency}}" },
        { label: "📅 Due Date", value: "{{dueDate}}" },
      ],
    )}<p>It won't take long — go ahead and submit so your team stays on track. ✅</p>${cta("Submit your report", THEMES.violet)}`,
  }),
  make({
    id: "report_reminder_rollup_manager",
    name: "Report Reminder — Roll-up (Manager)",
    category: "reports",
    icon: "📋",
    description:
      "Manager roll-up of direct reports who still owe their report for the cadence.",
    subject: "📋 {{totalCount}} of your team haven't submitted their report",
    eyebrow: "Heads up",
    title: "{{totalCount}} pending {{frequency}} report(s)",
    subtitle: "Still awaiting submission today.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{managerName}}</strong>,</p><p><strong>{{totalCount}}</strong> of your direct reports haven't submitted their <strong>{{frequency}}</strong> report yet.</p>",
    bodyHtml: `${dt([
      { label: "📅 Date", value: "{{todaysDate}}" },
      { label: "🔁 Frequency", value: "{{frequency}}" },
      { label: "📋 Pending", value: "<strong>{{totalCount}}</strong>" },
    ])}<p style="margin:16px 0 6px;font-weight:600;color:#0f172a;">Pending submissions</p>{{teamRowsHtml}}<p>A quick reminder to the team helps everyone stay on schedule. 🤝</p>${cta("View reports", THEMES.amber)}`,
  }),

  // ----- ASSETS -----
  make({
    id: "asset_assigned",
    name: "Asset Assigned (Employee)",
    category: "assets",
    icon: "💼",
    description: "Notify employee about a new asset assignment.",
    subject: "Asset Assigned to You",
    eyebrow: "Asset management",
    title: "{{assetName}} assigned to you",
    subtitle: "Please take good care of the asset.",
    accent: THEMES.teal,
    greeting:
      "<p>Hi <strong>{{employeeName}}</strong>,</p><p>This is to inform you that the following asset has been assigned to you.</p>",
    bodyHtml: `${dt([
      { label: "📦 Asset", value: "{{assetName}}" },
      { label: "🆔 Serial No", value: "{{serialNumber}}" },
      { label: "📂 Category", value: "{{categoryName}}" },
      { label: "👤 Assigned by", value: "{{assignedByName}}" },
      { label: "📅 Date", value: "{{assignedDate}}" },
    ])}<p>Please take good care of the asset. For any queries or concerns, feel free to reach out to your admin.</p>${cta("👉 View Asset Details", THEMES.teal)}`,
  }),
  make({
    id: "asset_acknowledge",
    name: "Asset Assignment + Acknowledgement (Employee)",
    category: "assets",
    icon: "📝",
    description: "Request signature on asset assignment acknowledgement form.",
    subject: "Action Required: Sign Asset Acknowledgement — {{assetName}}",
    eyebrow: "Action needed",
    title: "Sign the acknowledgement form",
    subtitle: "Confirm receipt of the assigned asset.",
    accent: THEMES.amber,
    greeting:
      "<p>Hi <strong>{{employeeName}}</strong>,</p><p>The following asset has been assigned to you. Please sign the acknowledgement form to confirm receipt.</p>",
    bodyHtml: `${dt([
      { label: "📦 Asset", value: "{{assetName}}" },
      { label: "🆔 Serial No", value: "{{serialNumber}}" },
      { label: "👤 Assigned by", value: "{{assignedByName}}" },
      { label: "📅 Date", value: "{{assignedDate}}" },
    ])}<p>Please complete this at your earliest. If you have any queries, feel free to reach out.</p>${cta("👉 Sign Here", THEMES.amber)}`,
  }),
  make({
    id: "asset_unassigned_admin",
    name: "Asset Unassigned (Admin)",
    category: "assets",
    icon: "🔓",
    description: "Alert admin that an asset has been unassigned from a user.",
    subject: "Asset Unassigned — {{assetName}}",
    eyebrow: "Asset management",
    title: "{{assetName}} has been unassigned",
    subtitle: "Reassign if this was a mistake.",
    accent: THEMES.slate,
    greeting:
      "<p>Hi <strong>{{adminName}}</strong>,</p><p>This is to inform you that the following asset has been unassigned from a user in the system.</p>",
    bodyHtml: `${dt([
      { label: "📦 Asset", value: "{{assetName}}" },
      { label: "👤 Unassigned From", value: "{{employeeName}}" },
      { label: "📅 Date", value: "{{unassignedDate}}" },
    ])}<p>If this was a mistake, please take action immediately to reassign the asset.</p>${cta("👉 View Dashboard", THEMES.slate)}`,
  }),
  make({
    id: "asset_employee_deleted_admin",
    name: "Employee Deleted (Admin)",
    category: "assets",
    icon: "🗑️",
    description:
      "Alert admin that an employee was removed, leaving assets unassigned.",
    subject: "Employee Deleted — {{employeeName}}",
    eyebrow: "Account removal",
    title: "{{employeeName}} has been removed",
    subtitle: "Review the assets that were assigned to them.",
    accent: THEMES.red,
    greeting:
      "<p>Hi <strong>{{adminName}}</strong>,</p><p>An employee account has been removed from the system.</p>",
    bodyHtml: `${dt([
      { label: "👤 Employee", value: "{{employeeName}}" },
      { label: "👤 Deleted By", value: "{{deletedByName}}" },
      { label: "📅 Date", value: "{{deletedDate}}" },
    ])}<p>Please note that assets assigned to this employee may now be unassigned. Review and reassign them at your earliest!</p>${cta("👉 View Assets", THEMES.red)}`,
  }),
  make({
    id: "asset_low_stock",
    name: "Low Stock Alert",
    category: "assets",
    icon: "⚠️",
    description: "Alert admin of stock falling below threshold.",
    subject: "Low Stock Alert — {{assetCategory}}",
    eyebrow: "Stock alert",
    title: "Stock running low",
    subtitle: "Please review and restock.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `<p>The stock for <strong>{{assetCategory}}</strong> has fallen below the minimum threshold.</p>${dt(
      [
        {
          label: "📦 Current Stock",
          value: "<strong>{{currentStock}} units</strong>",
        },
        { label: "📊 Threshold", value: "{{thresholdStock}} units" },
        { label: "📅 Date", value: "{{alertDate}}" },
      ],
    )}${cta("View assets", THEMES.amber)}`,
  }),
  make({
    id: "asset_warranty_expiring",
    name: "Warranty Expiring",
    category: "assets",
    icon: "📅",
    description: "Warn of asset warranty expiry.",
    subject: "⚠️ Warranty Expiring Soon — {{assetName}}",
    eyebrow: "Warranty alert",
    title: "Warranty expires soon",
    subtitle: "Take action before it's too late.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📦 Asset", value: "{{assetName}}" },
      { label: "📅 Expiry", value: "<strong>{{expiryDate}}</strong>" },
    ])}<p>Heads up! The warranty for <strong>{{assetName}}</strong> is expiring on {{expiryDate}}. Please take action!</p>${cta("View asset", THEMES.amber)}`,
  }),
  make({
    id: "asset_not_returned",
    name: "Asset Not Returned",
    category: "assets",
    icon: "🚫",
    description: "Asset return overdue after exit/transfer.",
    subject: "Asset Not Returned",
    eyebrow: "Action required",
    title: "{{employeeName}} has not returned {{assetName}}",
    subtitle: "Please follow up immediately.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📦 Asset", value: "{{assetName}}" },
      { label: "🆔 Serial No", value: "{{serialNumber}}" },
      { label: "👤 Assigned to", value: "{{employeeName}}" },
      { label: "📅 Return Deadline", value: "{{returnDeadline}}" },
      { label: "📋 Reason", value: "{{exitReason}}" },
    ])}${cta("View asset", THEMES.red)}`,
  }),

  // ----- TICKETS -----
  make({
    id: "ticket_raised_confirm",
    name: "Ticket Raised (Confirmation)",
    category: "tickets",
    icon: "🎫",
    description: "Confirm ticket received.",
    subject: "✅ Ticket Raised Successfully — #{{ticketId}}",
    eyebrow: "Ticket received",
    title: "Your ticket has been raised",
    subtitle: "We've received your support request.",
    accent: THEMES.indigo,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "<strong>#{{ticketId}}</strong>" },
      { label: "📋 Subject", value: "{{ticketSubject}}" },
      { label: "📌 Status", value: "Open" },
      { label: "📅 Raised on", value: "{{raisedDate}}" },
    ])}<p>Our support team will review your request and assign it shortly. You'll receive a notification once your ticket is assigned or updated.</p>${cta("View ticket")}`,
  }),
  make({
    id: "ticket_assigned",
    name: "Ticket Assigned",
    category: "tickets",
    icon: "🔧",
    description: "Notify team member of ticket assignment.",
    subject: "🔧 Ticket Assigned to You — #{{ticketId}}",
    eyebrow: "New assignment",
    title: "Ticket assigned to you",
    subtitle: "Please review and take action.",
    greeting: "<p>Hi <strong>{{teamMemberName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "<strong>#{{ticketId}}</strong>" },
      { label: "📋 Subject", value: "{{ticketSubject}}" },
      { label: "📝 Description", value: "{{ticketDescription}}" },
      { label: "👤 Raised by", value: "{{raisedByName}}" },
      { label: "📅 Date", value: "{{assignedDate}}" },
    ])}${cta("Open ticket")}`,
  }),
  make({
    id: "ticket_in_progress",
    name: "Ticket → In Progress",
    category: "tickets",
    icon: "⚡",
    description: "Notify ticket-raiser when work has begun.",
    subject: "🔄 Your Ticket is Now In Progress — #{{ticketId}}",
    eyebrow: "Status updated",
    title: "We're on it!",
    subtitle: "Our team is now working on your ticket.",
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "#{{ticketId}}" },
      { label: "👤 Assigned to", value: "{{teamMemberName}}" },
      {
        label: "📌 Status",
        value: "<strong style='color:#3730a3;'>In Progress</strong>",
      },
    ])}<p>You'll be notified again once your ticket is resolved.</p>${cta("View ticket")}`,
  }),
  make({
    id: "ticket_resolved",
    name: "Ticket Resolved",
    category: "tickets",
    icon: "✅",
    description: "Notify ticket-raiser of resolution.",
    subject: "✅ Ticket Resolved — #{{ticketId}}",
    eyebrow: "Resolved",
    title: "Your ticket has been resolved",
    subtitle: "Please confirm if your issue is sorted.",
    accent: THEMES.green,
    greeting: "<p>Hi <strong>{{employeeName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "#{{ticketId}}" },
      { label: "👤 Resolved by", value: "{{teamMemberName}}" },
      { label: "💬 Resolution", value: "{{resolutionNotes}}" },
      { label: "📅 Resolved on", value: "{{resolvedDate}}" },
    ])}${cta("View / Reopen ticket", THEMES.green)}<p style="font-size:13px;color:#64748b;">Satisfied? The ticket will auto-close in 48 hours. Still having issues? Reopen using the link above.</p>`,
  }),
  make({
    id: "ticket_overdue",
    name: "Ticket Overdue",
    category: "tickets",
    icon: "⚠️",
    description: "Alert admin of overdue ticket.",
    subject: "Overdue Ticket — #{{ticketId}}",
    eyebrow: "Action required",
    title: "Ticket overdue",
    subtitle: "This ticket has passed its due date.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "#{{ticketId}}" },
      { label: "👤 Raised by", value: "{{raisedByName}}" },
      { label: "📅 Due Date", value: "{{dueDate}}" },
    ])}<p>Please follow up or reassign at the earliest.</p>${cta("View ticket", THEMES.red)}`,
  }),
  make({
    id: "ticket_comment",
    name: "Ticket New Comment",
    category: "tickets",
    icon: "💬",
    description: "New comment on a ticket.",
    subject: "💬 New Comment on Ticket #{{ticketId}}",
    eyebrow: "New activity",
    title: "{{commenterName}} commented",
    subtitle: "Keep the conversation going.",
    bodyHtml: `${dt([
      { label: "🆔 Ticket ID", value: "#{{ticketId}}" },
      { label: "📋 Subject", value: "{{ticketSubject}}" },
      { label: "👤 Commented by", value: "{{commenterName}}" },
    ])}<blockquote style="border-left:3px solid #3730a3;padding:10px 14px;color:#475569;background:#f8fafc;border-radius:0 6px 6px 0;margin:12px 0;">{{commentText}}</blockquote>${cta("View & reply")}`,
  }),

  // ----- PROJECTS -----
  make({
    id: "project_created",
    name: "Project Created",
    category: "projects",
    icon: "📁",
    description: "Notify members of new project.",
    subject: "📁 New Project Created — {{projectName}}",
    eyebrow: "Project",
    title: "Welcome to {{projectName}}",
    subtitle: "A new project has been created and you've been added.",
    bodyHtml: `${dt([
      { label: "📋 Project", value: "{{projectName}}" },
      { label: "📅 Start", value: "{{startDate}}" },
      { label: "📅 End", value: "{{endDate}}" },
      { label: "🔴 Priority", value: "{{priority}}" },
      { label: "👑 Manager", value: "{{managerName}}" },
    ])}${cta("View project on dashboard")}<p>You're officially part of something great — let's build it together! 💪🎉</p>`,
  }),
  make({
    id: "project_deadline_changed",
    name: "Project Deadline Changed",
    category: "projects",
    icon: "📅",
    description: "Project end date updated.",
    subject: "📅 Project Deadline Updated — {{projectName}}",
    eyebrow: "Deadline change",
    title: "Deadline updated for {{projectName}}",
    subtitle: "Adjust your schedule accordingly.",
    accent: THEMES.amber,
    bodyHtml: `${dt([
      { label: "📋 Project", value: "{{projectName}}" },
      { label: "📅 Previous End Date", value: "{{previousEndDate}}" },
      { label: "📅 New End Date", value: "<strong>{{newEndDate}}</strong>" },
      { label: "✏️ Updated by", value: "{{managerName}}" },
    ])}${cta("View project", THEMES.amber)}`,
  }),
  make({
    id: "project_deadline_approaching",
    name: "Project Deadline Approaching",
    category: "projects",
    icon: "⏰",
    description: "3-day deadline reminder.",
    subject: "📅 Reminder: Project Deadline in 3 Days — {{projectName}}",
    eyebrow: "Heads up",
    title: "Deadline in 3 days",
    subtitle: "Ensure all tasks are on track.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "📅 Deadline", value: "{{deadline}}" },
      {
        label: "✅ Tasks Done",
        value: "<strong>{{tasksDone}} / {{tasksTotal}}</strong>",
      },
    ])}${cta("View project", THEMES.amber)}`,
  }),
  make({
    id: "project_deadline_overdue",
    name: "Project Deadline Overdue",
    category: "projects",
    icon: "🚨",
    description: "Project past deadline.",
    subject: "⚠️ Project Deadline Overdue — {{projectName}}",
    eyebrow: "Action required",
    title: "Project overdue",
    subtitle: "Please take immediate action.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "📅 Deadline", value: "{{deadline}}" },
      {
        label: "✅ Tasks Completed",
        value: "<strong>{{tasksDone}} of {{tasksTotal}}</strong>",
      },
    ])}${cta("View project", THEMES.red)}`,
  }),
  make({
    id: "project_no_tasks",
    name: "Project Has No Tasks",
    category: "projects",
    icon: "📋",
    description: "Project created but no tasks assigned.",
    subject: "📋 Action Required: No Tasks in Project — {{projectName}}",
    eyebrow: "Action needed",
    title: "{{projectName}} has no tasks",
    subtitle: "Add tasks to get the project started.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{managerName}}</strong>,</p>",
    bodyHtml: `<p>We noticed that the following project currently has no tasks assigned:</p>${dt(
      [
        { label: "📁 Project", value: "{{projectName}}" },
        { label: "📅 Deadline", value: "{{deadline}}" },
      ],
    )}<p>Please log in to your dashboard and add tasks to the project to ensure progress is tracked effectively.</p>${cta("View project", THEMES.amber)}`,
  }),
  make({
    id: "project_member_added_sample",
    name: "Added to Project",
    category: "projects",
    icon: "👥",
    description: "Notify new project member.",
    subject: "👋 You've Been Added to a Project — {{projectName}}",
    eyebrow: "Welcome aboard",
    title: "Welcome to {{projectName}}",
    subtitle: "You've been added to the project.",
    accent: THEMES.teal,
    bodyHtml: `${dt([
      { label: "📋 Project", value: "{{projectName}}" },
      { label: "👑 Your Role", value: "<strong>{{role}}</strong>" },
      { label: "✏️ Added by", value: "{{actorName}}" },
      { label: "📅 Start", value: "{{startDate}}" },
    ])}${cta("View project on dashboard", THEMES.teal)}<p>The team just got stronger! 🤝</p>`,
  }),

  // ----- SPRINTS -----
  make({
    id: "sprint_created",
    name: "Sprint Created",
    category: "projects",
    icon: "🚀",
    description: "New sprint kicked off.",
    subject: "🚀 New Sprint Created — {{sprintName}}",
    eyebrow: "Sprint",
    title: "{{sprintName}} created",
    subtitle: "The team just got stronger — let's crush this sprint together!",
    bodyHtml: `${dt([
      { label: "🚀 Sprint", value: "{{sprintName}}" },
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "📅 Start", value: "{{startDate}}" },
      { label: "📅 End", value: "{{endDate}}" },
      { label: "🎯 Stage", value: "Planning" },
    ])}${cta("View sprint on dashboard")}`,
  }),
  make({
    id: "sprint_active",
    name: "Sprint → Active",
    category: "projects",
    icon: "⚡",
    description: "Sprint moved to active stage.",
    subject: "⚡ Sprint Is Now Active — {{sprintName}}",
    eyebrow: "Sprint live",
    title: "{{sprintName}} is now Active",
    subtitle: "It's go time — let's make every day count!",
    accent: THEMES.green,
    bodyHtml: `${dt([
      { label: "🚀 Sprint", value: "{{sprintName}}" },
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "🔁 Stage", value: "Planning → <strong>Active</strong>" },
      { label: "📅 End", value: "{{endDate}}" },
    ])}${cta("View sprint", THEMES.green)}<p>Please flag any blockers early.</p>`,
  }),
  make({
    id: "sprint_completed",
    name: "Sprint → Completed",
    category: "projects",
    icon: "🏁",
    description: "Sprint successfully completed.",
    subject: "✅ Sprint Completed — {{sprintName}}",
    eyebrow: "Sprint done",
    title: "{{sprintName}} is complete",
    subtitle: "Outstanding work team — you absolutely crushed it!",
    accent: THEMES.green,
    bodyHtml: `${dt([
      { label: "🚀 Sprint", value: "{{sprintName}}" },
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "📅 Completion", value: "{{completionDate}}" },
      { label: "✏️ Closed by", value: "{{managerName}}" },
    ])}${cta("View report", THEMES.green)}<p>Great things are built one completed sprint at a time! 🎉🏆</p>`,
  }),
  make({
    id: "sprint_overdue",
    name: "Sprint Overdue",
    category: "projects",
    icon: "🚨",
    description: "Sprint past its end date.",
    subject: "🚨 Sprint Overdue — {{sprintName}}",
    eyebrow: "Action needed",
    title: "Sprint past its end date",
    subtitle: "Take action to complete or update timeline.",
    accent: THEMES.red,
    bodyHtml: `${dt([
      { label: "🚀 Sprint", value: "{{sprintName}}" },
      { label: "📁 Project", value: "{{projectName}}" },
      { label: "📅 End Date", value: "{{endDate}}" },
      { label: "🎯 Stage", value: "{{currentStage}}" },
    ])}${cta("View sprint", THEMES.red)}<p>Let's act fast and get this back on track! ⚠️</p>`,
  }),

  // ----- CALENDAR -----
  make({
    id: "calendar_event_created",
    name: "Event Created",
    category: "calendar",
    icon: "📅",
    description: "Confirm event added to calendar.",
    subject: "Event Created — {{eventTitle}}",
    eyebrow: "Calendar",
    title: "{{eventTitle}}",
    subtitle: "Your event has been added to your calendar!",
    bodyHtml: `${dt([
      { label: "🏷 Category", value: "{{eventCategory}}" },
      { label: "📅 Date", value: "{{eventDate}}" },
      { label: "⏰ Time", value: "{{startTime}} - {{endTime}}" },
    ])}<p>You'll get a reminder when it starts!</p>${cta("View event")}`,
  }),
  make({
    id: "calendar_event_reminder",
    name: "Event Reminder (10 min)",
    category: "calendar",
    icon: "🔔",
    description: "Reminder 10 mins before event.",
    subject: "Your Event is Starting Now — {{eventTitle}}",
    eyebrow: "Starting soon",
    title: "{{eventTitle}}",
    subtitle: "Starting at {{startTime}}!",
    accent: THEMES.amber,
    bodyHtml: `${dt([
      { label: "🏷 Category", value: "{{eventCategory}}" },
      { label: "📅 Date", value: "{{eventDate}}" },
      { label: "⏰ Time", value: "{{startTime}}" },
    ])}<p>Have a productive session! 💪</p>`,
  }),
  make({
    id: "calendar_event_tomorrow",
    name: "Event Tomorrow",
    category: "calendar",
    icon: "📆",
    description: "Heads-up about tomorrow's event.",
    subject: "📅 Event Tomorrow — {{eventTitle}}",
    eyebrow: "Tomorrow",
    title: "{{eventTitle}}",
    subtitle: "You have an event scheduled tomorrow.",
    bodyHtml: `${dt([
      { label: "🏷 Category", value: "{{eventCategory}}" },
      { label: "📅 Date", value: "{{eventDate}}" },
      { label: "⏰ Time", value: "{{startTime}} - {{endTime}}" },
    ])}<p>Don't forget to prepare ahead of time!</p>${cta("View event")}`,
  }),
  make({
    id: "calendar_event_allday",
    name: "All Day Event Today",
    category: "calendar",
    icon: "☀️",
    description: "Heads-up for an all-day event.",
    subject: "All Day Event Today — {{eventTitle}}",
    eyebrow: "Today",
    title: "{{eventTitle}}",
    subtitle: "You have an all-day event today!",
    bodyHtml: `${dt([
      { label: "🏷 Category", value: "{{eventCategory}}" },
      { label: "📅 Date", value: "{{eventDate}}" },
    ])}<p>Have a great day! 💪</p>${cta("View event")}`,
  }),

  // ----- DRIVE / FILE MANAGER -----
  make({
    id: "drive_file_shared",
    name: "File Shared",
    category: "drive",
    icon: "📂",
    description: "Notify user of shared file.",
    subject: "📂 A File Has Been Shared with You — {{fileName}}",
    eyebrow: "File sharing",
    title: "{{sharedByName}} shared a file with you",
    subtitle: "Head over and check it out!",
    bodyHtml: `${dt([
      { label: "📄 File", value: "{{fileName}}" },
      { label: "📁 Type", value: "{{fileType}}" },
      { label: "👤 Shared by", value: "{{sharedByName}}" },
      { label: "📅 Shared on", value: "{{sharedDate}}" },
    ])}${cta("View file in file manager")}`,
  }),
  make({
    id: "drive_storage_80",
    name: "Storage 80% Used",
    category: "drive",
    icon: "⚠️",
    description: "Warn admin of approaching storage limit.",
    subject: "⚠️ Storage Warning: 80% of CMS Drive Used",
    eyebrow: "Storage alert",
    title: "Storage running low",
    subtitle: "Please take action to avoid disruption.",
    accent: THEMES.amber,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📦 Used", value: "<strong>{{usedStorage}}</strong>" },
      { label: "📦 Total", value: "{{totalStorage}}" },
      {
        label: "📊 Usage",
        value: "<strong style='color:#b45309;'>80%</strong>",
      },
    ])}<p>We recommend deleting unused files or folders.</p>${cta("View file manager", THEMES.amber)}`,
  }),
  make({
    id: "drive_storage_full",
    name: "Storage Full",
    category: "drive",
    icon: "🚨",
    description: "Critical alert: storage at 100%.",
    subject: "🚨 Critical: CMS Drive Storage Full",
    eyebrow: "Critical",
    title: "Drive storage full",
    subtitle: "All file uploads are currently blocked.",
    accent: THEMES.red,
    greeting: "<p>Hi <strong>{{adminName}}</strong>,</p>",
    bodyHtml: `${dt([
      { label: "📦 Used", value: "{{usedStorage}}" },
      { label: "📦 Total", value: "{{totalStorage}}" },
      {
        label: "📊 Usage",
        value: "<strong style='color:#dc2626;'>100%</strong>",
      },
      { label: "📦 Remaining", value: "0 MB" },
    ])}<p>Immediate action is required. Please delete unnecessary files to restore upload access for your team.</p>${cta("View file manager", THEMES.red)}`,
  }),
  make({
    id: "drive_official_document",
    name: "Official Document Added",
    category: "drive",
    icon: "📄",
    description: "Announce new official document.",
    subject: "📄 New Official Document Added — {{fileName}}",
    eyebrow: "Mandatory read",
    title: "New official document",
    subtitle: "Please review at your earliest.",
    bodyHtml: `${dt([
      { label: "📄 File", value: "{{fileName}}" },
      { label: "📅 Added", value: "{{addedDate}}" },
    ])}<p>Please log in to read the document and take any required action.</p>${cta("View official document")}`,
  }),

  // ----- CHAT -----
  make({
    id: "chat_mentioned",
    name: "Mentioned in Chat",
    category: "chat",
    icon: "💬",
    description: "You were @mentioned.",
    subject: "💬 You Were Mentioned in a Chat — {{chatName}}",
    eyebrow: "Mention",
    title: "{{senderName}} mentioned you",
    subtitle: "Jump in and respond.",
    accent: THEMES.blue,
    bodyHtml: `${dt([
      { label: "👥 Chat", value: "{{chatName}}" },
      { label: "👤 Mentioned by", value: "{{senderName}}" },
    ])}<p>Please log in to review the message and respond if any action is required.</p>${cta("Open chat", THEMES.blue)}`,
  }),
  make({
    id: "chat_group_added",
    name: "Added to Group",
    category: "chat",
    icon: "👥",
    description: "You've been added to a new chat group.",
    subject: "You've Been Added to a Chat Group — {{groupName}}",
    eyebrow: "New group",
    title: "Welcome to {{groupName}}",
    subtitle: "Jump in and say hello to the team!",
    accent: THEMES.blue,
    bodyHtml: `${dt([
      { label: "👥 Group", value: "{{groupName}}" },
      { label: "👤 Added by", value: "{{adminName}}" },
      { label: "📅 Date", value: "{{addedDate}}" },
    ])}${cta("Open chat", THEMES.blue)}`,
  }),
  make({
    id: "chat_unread_summary",
    name: "Unread Messages",
    category: "chat",
    icon: "📬",
    description: "Catch-up reminder after 2 days of inactivity.",
    subject: "You Have Unread Messages — Quick Catch Up!",
    eyebrow: "Catch up",
    title: "You have unread messages",
    subtitle: "Don't miss out — jump back in!",
    accent: THEMES.blue,
    bodyHtml: `${dt([{ label: "📬 Unread Messages", value: "<strong>{{unreadCount}}</strong>" }])}<p>You have not visited the chat in the last 2 days. Please log in to review your messages.</p>${cta("Open chat", THEMES.blue)}`,
  }),
];
